import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useConnectionState, ZeroProvider } from "@rocicorp/zero/react";
import { Loader2 } from "lucide-react";
import {
  appMutators,
  appSchema,
  type AppZeroContext,
} from "@production-hub/shared";
import type { CustomMutatorDefs } from "@rocicorp/zero";
import { useAuth } from "@/hooks/useAuth";
import { useAuthToken } from "@/hooks/useAuthToken";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";

const zeroCacheURL = import.meta.env.VITE_ZERO_CACHE_URL as string | undefined;
const zeroQueryURL =
  (import.meta.env.VITE_ZERO_QUERY_URL as string | undefined) ??
  "/api/zero/query";
const zeroMutateURL =
  (import.meta.env.VITE_ZERO_MUTATE_URL as string | undefined) ??
  "/api/zero/mutate";

// Minimum gap between needs-auth–triggered force-refreshes, to avoid a
// tight loop if the server keeps rejecting the refreshed token.
const NEEDS_AUTH_REFRESH_COOLDOWN_MS = 5_000;
const ZERO_RECONNECT_WATCHDOG_MS = 15_000;

function ZeroStartupLoading({ message }: { message: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <div className="text-center">
          <p className="text-sm font-medium">{message}</p>
          <p className="text-xs text-muted-foreground">
            Local data will appear as soon as sync is ready.
          </p>
        </div>
      </div>
    </div>
  );
}

function ZeroConfigurationError() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold mb-2">Sync is not configured</h1>
        <p className="text-sm text-muted-foreground">
          The app was built without VITE_ZERO_CACHE_URL. Rebuild the dev
          environment so Zero-backed screens can load.
        </p>
      </div>
    </div>
  );
}

function NeedsAuthListener({
  children,
  onNeedsAuth,
}: {
  children: ReactNode;
  onNeedsAuth: () => void;
}) {
  const connectionState = useConnectionState();
  const lastTriggeredRef = useRef(0);

  useEffect(() => {
    if (connectionState.name !== "needs-auth") return;
    const now = Date.now();
    if (now - lastTriggeredRef.current < NEEDS_AUTH_REFRESH_COOLDOWN_MS) return;
    lastTriggeredRef.current = now;
    onNeedsAuth();
  }, [connectionState.name, onNeedsAuth]);

  return <>{children}</>;
}

function ZeroReconnectMonitor({
  children,
  onReconnectStuckConnection,
}: {
  children: ReactNode;
  onReconnectStuckConnection: () => void;
}) {
  const connectionState = useConnectionState();
  const { isOnline } = useOnlineStatus();

  useEffect(() => {
    if (connectionState.name !== "disconnected") return;
    if (!isOnline) return;

    const timeout = window.setTimeout(() => {
      if (typeof navigator !== "undefined" && !navigator.onLine) return;
      onReconnectStuckConnection();
    }, ZERO_RECONNECT_WATCHDOG_MS);

    return () => window.clearTimeout(timeout);
  }, [connectionState.name, isOnline, onReconnectStuckConnection]);

  return <>{children}</>;
}

export function AppZeroProvider({ children }: { children: ReactNode }) {
  const { user, role, personId, isLoading } = useAuth();
  const { token, forceRefresh } = useAuthToken();
  const [reconnectKey, setReconnectKey] = useState(0);
  const wasOfflineRef = useRef(
    typeof navigator !== "undefined" ? !navigator.onLine : false,
  );

  useEffect(() => {
    if (!zeroCacheURL && import.meta.env.DEV) {
      console.warn(
        "VITE_ZERO_CACHE_URL is not configured; app Zero sync is disabled.",
      );
    }
  }, []);

  const context: AppZeroContext | null = useMemo(
    () =>
      user?.id
        ? {
            userID: user.id,
            personID: personId,
            role,
          }
        : null,
    [personId, role, user?.id],
  );

  const handleNeedsAuth = useCallback(() => {
    void forceRefresh();
  }, [forceRefresh]);
  const zeroAuth = useMemo(() => (token ? `Bearer ${token}` : null), [token]);

  useEffect(() => {
    if (!user?.id || token) return;
    void forceRefresh();
  }, [forceRefresh, token, user?.id]);

  const remountZero = useCallback(() => {
    void forceRefresh();
    setReconnectKey((key) => key + 1);
  }, [forceRefresh]);

  useEffect(() => {
    const handleOffline = () => {
      wasOfflineRef.current = true;
    };
    const handleOnline = () => {
      if (!wasOfflineRef.current) return;
      wasOfflineRef.current = false;
      void forceRefresh();
    };

    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);

    return () => {
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
    };
  }, [forceRefresh]);

  if (isLoading) return <ZeroStartupLoading message="Restoring session" />;
  if (!user?.id || !context) return <>{children}</>;
  if (!zeroCacheURL) return <ZeroConfigurationError />;
  if (!zeroAuth) return <ZeroStartupLoading message="Preparing sync" />;

  return (
    <ZeroProvider
      key={`${user.id}:${reconnectKey}`}
      userID={user.id}
      auth={zeroAuth}
      schema={appSchema}
      mutators={appMutators as unknown as CustomMutatorDefs}
      cacheURL={zeroCacheURL}
      queryURL={zeroQueryURL}
      mutateURL={zeroMutateURL}
      context={context}
    >
      <NeedsAuthListener onNeedsAuth={handleNeedsAuth}>
        <ZeroReconnectMonitor onReconnectStuckConnection={remountZero}>
          {children}
        </ZeroReconnectMonitor>
      </NeedsAuthListener>
    </ZeroProvider>
  );
}
